/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    LIST_ASSETS,
    PermissionValues,
    useAssetListFilter,
    useAssetListOrder,
    useCurrentAccount,
    useCurrentWorkspace,
    useSetAssetListOrder,
    WorkspaceType,
} from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteDataGrid, SelectionStatus } from '../infiniteDataGrid';
import { useColumns } from './useColumns';

export type AssetListProps = {
  onAssetClick: any;
  maxHeight?: number;
  paddingBottom?: number;
  onAssetsCountChange?: (count: number) => void;
  updateRowsSelected?: (rows: Array<any>) => void;
  preserveScrollKey?: string;
  addToGroupsComponent?: React.ReactNode;
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
  onDataLoaded?: (data: any) => void;
  customEmptyState?: React.ReactNode;
  routinesButton?: (data: any) => React.ReactElement;
  addonsButton?: (data: any) => React.ReactElement;
  alertsButton?: (data: {
    alertsCount: number;
    assetId: string;
    name: string;
  }) => React.ReactElement;
};

export const AssetsListOld = ({
  onAssetClick,
  maxHeight,
  paddingBottom,
  onAssetsCountChange,
  updateRowsSelected,
  preserveScrollKey,
  addToGroupsComponent,
  selectionStatus,
  onSelectionStatusChange,
  onDataLoaded,
  customEmptyState,
  routinesButton,
  addonsButton,
  alertsButton,
}: AssetListProps) => {
  const { t } = useTranslation();

  const { data } = useAssetListFilter();
  const { data: orderData } = useAssetListOrder();
  const { mutateAsync: setOrder } = useSetAssetListOrder();
  const workspace = useCurrentWorkspace(true);
  const { account } = useCurrentAccount();
  const showImpersonateWorkspace = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  const setOrderData = useCallback(
    (order: any) => {
      setOrder(order);
    },
    [setOrder]
  );

  const [filter, setFilter] = useState<any>(undefined);
  const { columns, exportColumns } = useColumns(
    showImpersonateWorkspace,
    filter,
    routinesButton,
    addonsButton,
    alertsButton
  ) as {
    columns: ColumnDef<any, any>[];
    exportColumns: ColumnDef<any, any>[];
  };

  useEffect(() => {
    if (!data || workspace === undefined) return;

    const isOEM = workspace?.type === WorkspaceType.Oem;
    const f = { ...data };
    f.workspaceId = workspace?.id;
    if (f.keyword === '') delete f.keyword;
    if (f.groups?.length === 0) delete f.groups;
    
    // Filter out productName and addons if not OEM
    if (f.columns && !isOEM) {
      f.columns = f.columns.filter(
        (col: string) => col !== 'productName' && col !== 'addons'
      );
    }
    setFilter(f);
  }, [data, workspace]);

  return !filter ? null : (
    <InfiniteDataGrid
      modelName="asset"
      query={LIST_ASSETS}
      orderBy={orderData}
      filter={filter}
      pageSize={50}
      columns={columns}
      onRowClick={onAssetClick}
      listMaxHeight={maxHeight as number}
      paddingBottom={paddingBottom as number}
      textEmpty={customEmptyState || t('assets.textEmpty')}
      onItemsCountChange={onAssetsCountChange}
      dataPrefix="asset.listAssets"
      onOrderByChange={setOrderData}
      updateRowsSelected={workspace ? updateRowsSelected : undefined}
      enableScrollRefresh={true}
      preserveScrollKey={preserveScrollKey}
      showScrollIndicator={true}
      headerContent={addToGroupsComponent}
      exportColumns={exportColumns}
      exportFileName="assets"
      selectionStatus={selectionStatus}
      onSelectionStatusChange={onSelectionStatusChange}
      onDataLoaded={onDataLoaded}
    />
  );
};
